class HomepageCollectionExperience extends HTMLElement {
  connectedCallback() {
    this.buttons = Array.from(this.querySelectorAll('[data-quick-filter-btn]'));
    this.subButtons = Array.from(this.querySelectorAll('[data-sub-filter-btn]'));
    this.toggleButtons = Array.from(this.querySelectorAll('[data-collection-toggle-btn]'));
    if (!this.buttons.length) return;

    this.activeClass = 'is-active';
    this.gridContainer = this.querySelector('#ProductGridContainer');
    this.titleElement = this.querySelector('[data-quick-filter-title]');
    this.descriptionElement = this.querySelector('[data-quick-filter-description]');

    this.primaryUrl = this.dataset.primaryCollectionUrl || '';
    this.mirrorUrl = this.dataset.mirrorCollectionUrl || '';
    this.collectionSectionKey = this.dataset.collectionSectionId || 'product-grid';
    this.productsPerPage = parseInt(this.dataset.productsPerPage, 10) || 12;
    this.activeCollectionTarget = 'primary';

    this.cache = new Map();
    this.sectionIds = new Map();
    this.sectionIdPromises = new Map();
    this.requestCounter = 0;
    this.swapGeneration = 0;
    this.abortController = null;
    // "param=value" filter pairs with zero matches in the current category view,
    // rebuilt from facet counts on each parent switch (see collectEmptyFilterValues).
    this.emptyFilterValues = new Set();

    this.handlePillClick = this.onPillClick.bind(this);
    this.handleSubClick = this.onSubClick.bind(this);
    this.handleToggleClick = this.onToggleClick.bind(this);
    this.handlePaginationClick = this.onPaginationClick.bind(this);

    this.buttons.forEach((btn) => btn.addEventListener('click', this.handlePillClick));
    this.subButtons.forEach((btn) => btn.addEventListener('click', this.handleSubClick));
    this.toggleButtons.forEach((btn) => btn.addEventListener('click', this.handleToggleClick));
    if (this.gridContainer) {
      this.gridContainer.addEventListener('click', this.handlePaginationClick);
    }

    this.updateSubFilterVisibility(this.getActiveParentButton());

    // Discover the real section_id for each collection in the background so
    // the first user click doesn't pay the discovery cost.
    if (this.primaryUrl) this.ensureSectionId(this.primaryUrl);
    if (this.mirrorUrl) this.ensureSectionId(this.mirrorUrl);

    // The grid sits below the hero, so its lazy images would pop in one-by-one as
    // you scroll to it. Force the first row to load up front (see below).
    this.upgradeAboveFoldImages();
    this.fetchInitialGrid();
  }

  disconnectedCallback() {
    this.buttons.forEach((btn) => btn.removeEventListener('click', this.handlePillClick));
    this.subButtons.forEach((btn) => btn.removeEventListener('click', this.handleSubClick));
    this.toggleButtons.forEach((btn) => btn.removeEventListener('click', this.handleToggleClick));
    if (this.gridContainer && this.handlePaginationClick) {
      this.gridContainer.removeEventListener('click', this.handlePaginationClick);
    }
    if (this.abortController) this.abortController.abort();
  }

  getActiveCollectionUrl() {
    return this.activeCollectionTarget === 'mirror' ? this.mirrorUrl : this.primaryUrl;
  }

  getActiveParentButton() {
    return this.buttons.find((btn) => btn.classList.contains(this.activeClass)) || this.getDefaultButton();
  }

  getDefaultButton() {
    return this.buttons.find((btn) => btn.dataset.default === 'true') || this.buttons[0];
  }

  getDefaultSubButton() {
    if (!this.subButtons.length) return null;
    return this.subButtons.find((btn) => btn.dataset.default === 'true') || this.subButtons[0];
  }

  // The server-rendered grid is a plain product loop with no {% paginate %},
  // so it's capped at products_per_page and never includes pagination. On load
  // we reconcile it with the canonical collection section — exactly what a pill
  // or filter click fetches — so the active sub-filter's sort is applied and the
  // pager appears whenever there are more products. Previously this only ran
  // when the default sub-filter carried a `filter.` param, which left the
  // default ALL + Best Sellers view (sort only) without a pager.
  fetchInitialGrid() {
    const activeParent = this.getActiveParentButton();
    const defaultSub = this.getDefaultSubButton();
    // Reconcile silently — the server-rendered grid is already on screen, so
    // suppress the loading overlay (it would fade out fully-rendered content on
    // first paint). User-initiated fetches still show the overlay for feedback.
    this.fetchAndSwap(this.buildSearchParams(activeParent, defaultSub).toString(), {
      showLoading: false,
      refreshSubFilters: true,
    });
  }

  onPillClick(event) {
    event.preventDefault();
    const button = event.currentTarget;
    if (button.classList.contains(this.activeClass)) return;

    const subToApply = this.getDefaultSubButton();
    const params = this.buildSearchParams(button, subToApply);

    this.setActiveButton(button);
    this.setActiveSubButton(subToApply);
    this.updateSubFilterVisibility(button);
    this.updateHeadingFromButton(button);
    this.fetchAndSwap(params.toString(), { refreshSubFilters: true });
  }

  onSubClick(event) {
    event.preventDefault();
    const subButton = event.currentTarget;
    if (subButton.classList.contains(this.activeClass)) return;

    const activeParent = this.getActiveParentButton();
    const params = this.buildSearchParams(activeParent, subButton);

    this.setActiveSubButton(subButton);
    this.fetchAndSwap(params.toString());
  }

  onToggleClick(event) {
    event.preventDefault();
    const button = event.currentTarget;
    if (button.classList.contains(this.activeClass)) return;
    const target = button.dataset.collectionTarget;
    if (!target) return;
    if (target === 'mirror' && !this.mirrorUrl) return;

    this.activeCollectionTarget = target;

    this.toggleButtons.forEach((btn) => {
      const isActive = btn === button;
      btn.classList.toggle(this.activeClass, isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    const defaultParent = this.getDefaultButton();
    const defaultSub = this.getDefaultSubButton();
    this.setActiveButton(defaultParent);
    this.setActiveSubButton(defaultSub);
    this.updateSubFilterVisibility(defaultParent);
    this.updateHeadingFromButton(defaultParent);

    const params = this.buildSearchParams(defaultParent, defaultSub);
    this.fetchAndSwap(params.toString(), { refreshSubFilters: true });
  }

  buildSearchParams(parentButton, subButton = null) {
    const params = new URLSearchParams();

    if (parentButton) {
      const parentParams = this.parseParams(parentButton.dataset.query);
      parentParams.forEach((value, key) => {
        if (value !== '') params.append(key, value);
      });
    }

    if (subButton) {
      const useAllOverride = parentButton && parentButton.dataset.default === 'true' && subButton.dataset.queryAllOverride;
      const subQuery = useAllOverride ? subButton.dataset.queryAllOverride : subButton.dataset.query;
      const subParams = this.parseParams(subQuery);
      subParams.forEach((value, key) => {
        if (value !== '') params.append(key, value);
      });
    }

    return params;
  }

  parseParams(queryString) {
    return new URLSearchParams(queryString || '');
  }

  setActiveButton(activeButton) {
    if (!activeButton) return;
    this.buttons.forEach((btn) => {
      const isActive = btn === activeButton;
      btn.classList.toggle(this.activeClass, isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  setActiveSubButton(activeSubButton) {
    if (!activeSubButton) return;
    this.subButtons.forEach((btn) => {
      const isActive = btn === activeSubButton;
      btn.classList.toggle(this.activeClass, isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  updateSubFilterVisibility(parentButton) {
    if (!this.subButtons.length || !parentButton) return;
    const hideList = (parentButton.dataset.hideSubfilters || '')
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    this.subButtons.forEach((subBtn) => {
      const label = subBtn.textContent.trim().toLowerCase();
      const hidden = hideList.includes(label) || this.isSubFilterEmpty(subBtn);
      subBtn.style.display = hidden ? 'none' : '';
    });
  }

  // A sub-filter is "empty" when a metafield/tag filter in its query has zero
  // matches in the current category view. `emptyFilterValues` is rebuilt from the
  // facet counts of the latest parent fetch (see collectEmptyFilterValues).
  // Sort-only sub-filters (Best Sellers, New Arrivals) carry no `filter.` param,
  // so they are never treated as empty.
  isSubFilterEmpty(subBtn) {
    if (!this.emptyFilterValues || !this.emptyFilterValues.size) return false;
    const params = new URLSearchParams(subBtn.dataset.query || '');
    for (const [key, value] of params) {
      if (key.indexOf('filter.') === 0 && this.emptyFilterValues.has(`${key}=${value}`)) {
        return true;
      }
    }
    return false;
  }

  // Read per-value product counts from the just-fetched collection section's
  // facets. facets.liquid renders a count-0 (and inactive) value with a `disabled`
  // class on its .facets__label plus a trailing "(0)" — both are checked. Returns
  // a Set of "param=value" strings (e.g. "filter.p.tag=sale") that have no matches
  // in the current category view.
  collectEmptyFilterValues(parsed) {
    const empty = new Set();
    parsed.querySelectorAll('label.facets__label').forEach((label) => {
      const input = label.querySelector('input[name^="filter."]');
      if (!input || !input.name || !input.value) return;
      let isEmpty = label.classList.contains('disabled');
      if (!isEmpty) {
        const match = label.textContent.match(/\((\d+)\)/);
        if (match && parseInt(match[1], 10) === 0) isEmpty = true;
      }
      if (isEmpty) empty.add(`${input.name}=${input.value}`);
    });
    return empty;
  }

  updateHeadingFromButton(button) {
    if (!button) return;
    if (this.titleElement) {
      const heading = button.dataset.heading || this.titleElement.dataset.defaultHeading || '';
      this.titleElement.textContent = heading;
    }
    if (this.descriptionElement) {
      const description = button.dataset.description || '';
      this.descriptionElement.textContent = description;
      this.descriptionElement.toggleAttribute('hidden', description === '');
    }
  }

  // Shopify renders sections inside JSON templates with a prefixed ID like
  // `template--<template-id>__<key>`. We can't know that ID server-side from
  // the homepage, so we fetch the full collection page once per collection
  // and extract the real ID from the product-grid element. The result is
  // memoized — subsequent AJAX calls reuse it.
  ensureSectionId(collectionUrl) {
    if (this.sectionIds.has(collectionUrl)) {
      return Promise.resolve(this.sectionIds.get(collectionUrl));
    }
    if (this.sectionIdPromises.has(collectionUrl)) {
      return this.sectionIdPromises.get(collectionUrl);
    }
    const promise = (async () => {
      try {
        const response = await fetch(collectionUrl);
        const text = await response.text();
        const match = text.match(/id="product-grid"\s+data-id="([^"]+)"/);
        const sectionId = (match && match[1]) || this.collectionSectionKey;
        this.sectionIds.set(collectionUrl, sectionId);
        return sectionId;
      } catch (e) {
        return this.collectionSectionKey;
      }
    })();
    this.sectionIdPromises.set(collectionUrl, promise);
    return promise;
  }

  async fetchAndSwap(searchParams, { showLoading = true, refreshSubFilters = false } = {}) {
    const collectionUrl = this.getActiveCollectionUrl();
    if (!collectionUrl) return;

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    const requestId = ++this.requestCounter;

    if (showLoading) this.setLoading(true);

    try {
      const sectionId = await this.ensureSectionId(collectionUrl);
      if (requestId !== this.requestCounter) return;

      const url = `${collectionUrl}?section_id=${encodeURIComponent(sectionId)}${searchParams ? '&' + searchParams : ''}`;
      let parsed;
      let fromCache = false;
      if (this.cache.has(url)) {
        parsed = this.cache.get(url);
        fromCache = true;
      } else {
        const response = await fetch(url, { signal: this.abortController.signal });
        const text = await response.text();
        parsed = new DOMParser().parseFromString(text, 'text/html');
        if (this.cache.size >= 20) {
          this.cache.delete(this.cache.keys().next().value);
        }
        this.cache.set(url, parsed);
      }
      if (requestId !== this.requestCounter) return;
      // On a parent switch (or initial load), recompute which sub-filters have
      // zero matches in the new category view, then re-hide their pills. The
      // active parent applies its default sort-only sub, so these facet counts
      // reflect the category alone — what each sub-filter pill should gate on.
      if (refreshSubFilters) {
        this.emptyFilterValues = this.collectEmptyFilterValues(parsed);
      }
      // Cached categories render instantly (no dim/decode); only a fresh fetch gets
      // the background-load treatment.
      this.swapGrid(parsed, fromCache);
      if (refreshSubFilters) {
        this.updateSubFilterVisibility(this.getActiveParentButton());
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('homepage-collection-experience fetch failed:', error);
      // Clear the overlay ONLY on a real error (no swap will happen). On success,
      // swapGrid's finish() drops it atomically with the grid swap — clearing it
      // here (the old finally) yanked the overlay before the new grid was ready,
      // which read as a flicker.
      if (requestId === this.requestCounter && showLoading) this.setLoading(false);
    }
  }

  // Force the first row of product images to load up front instead of lazily, so the
  // below-the-hero grid doesn't pop in one image at a time as you scroll to it. First
  // row = 4 cards on desktop (4-up), 2 on mobile (2-up). card-product already renders
  // the first 2 eager, so on desktop this upgrades cards 3–4. Setting eager on the
  // real card <img> (not a separate preload) is what makes the below-fold image
  // actually paint before you arrive at it.
  upgradeAboveFoldImages() {
    if (!this.gridContainer) return;
    const eagerCount = window.matchMedia('(min-width: 990px)').matches ? 4 : 2;
    const items = Array.from(this.gridContainer.querySelectorAll('#product-grid > li')).slice(0, eagerCount);
    items.forEach((li) => {
      const img =
        li.querySelector('.card-image-slider__list .slider__slide:first-child img') || li.querySelector('img');
      if (img && img.getAttribute('loading') !== 'eager') {
        img.setAttribute('loading', 'eager');
        img.loading = 'eager';
      }
    });
  }

  // Double-buffer the grid swap (mirrors FacetFiltersForm.swapGridWhenReady on the
  // collection page): keep the current grid visible (dimmed) while the incoming
  // grid loads HIDDEN in the background; once its above-the-fold images decode,
  // reveal the whole new grid at once. No blank gap, no per-image popcorn, no
  // fade-in. Matches: click -> grid dims -> new grid loads in background -> whole
  // new grid appears together.
  swapGrid(parsed, instant = false) {
    if (!this.gridContainer) return;
    const parsedContainer = parsed.getElementById('ProductGridContainer');
    if (!parsedContainer) return;

    parsedContainer.querySelectorAll('.scroll-trigger').forEach((el) => {
      el.classList.add('scroll-trigger--cancel');
    });

    // Preserve the collection page's full page size (typically 36) so the
    // injected pagination matches the products shown. Slicing here breaks
    // navigation because Shopify only exposes per-page granularity, not
    // arbitrary count slicing — sliced-off products become unreachable.
    const newHTML = parsedContainer.innerHTML;
    const container = this.gridContainer;
    const generation = ++this.swapGeneration;
    let stage = null;

    const apply = () => {
      // Remove the swap state BEFORE inserting so the incoming #product-grid is
      // created at full opacity and appears at once with NO fade-in. The fresh
      // .collection has no .loading class, so the swap also drops the loading
      // overlay ATOMICALLY with the new grid (no flicker); clear aria-busy too,
      // since innerHTML leaves the container's own attributes untouched.
      container.classList.remove('is-swapping');
      container.innerHTML = newHTML;
      container.setAttribute('aria-busy', 'false');
      // Re-apply first-row eager loading to the freshly swapped-in grid.
      this.upgradeAboveFoldImages();
    };

    // Cached render: the section HTML + its images are already warm (this category
    // was viewed earlier this session), so swap instantly — the dim + background
    // decode would just be a needless loading flash.
    if (instant) {
      apply();
      return;
    }

    const finish = () => {
      if (stage && stage.parentNode) stage.remove();
      // A newer swap superseded this one (user clicked again during the background
      // load) — drop this stale result instead of overwriting the newer grid.
      if (generation !== this.swapGeneration) return;
      apply();
    };

    try {
      container.classList.add('is-swapping'); // dim the outgoing grid (stays on screen — no blank)

      // Above-the-fold ≈ 2 rows, but rows-per-screen depend on the column count,
      // which differs by breakpoint (4-up desktop vs 2-up mobile). Derive the
      // per-row count from the live grid + current breakpoint so we don't over-wait
      // on mobile (a fixed 8 ≈ 4 rows there).
      let perRow = 4;
      const liveGrid = container.querySelector('#product-grid');
      if (liveGrid) {
        const isDesktop = window.matchMedia('(min-width: 990px)').matches;
        const match = liveGrid.className.match(
          isDesktop ? /grid--(\d+)-col-desktop/ : /grid--(\d+)-col-tablet-down/
        );
        if (match) perRow = parseInt(match[1], 10) || perRow;
      }
      const aboveFold = Math.max(2, perRow * 2);

      // Warm the cache by preloading ONLY the above-the-fold images as bare <img>
      // clones — NOT the card markup. Staging full cards would connect their custom
      // elements (modal-dialog, sliders, …) in the wrong context and throw. Bare
      // imgs carry the same srcset+sizes, so they fetch the exact candidate the real
      // grid will use; the final swap then paints those images together from cache.
      const sources = Array.from(
        parsedContainer.querySelectorAll('.card-image-slider__list .slider__slide:first-child img')
      ).slice(0, aboveFold);
      if (!sources.length) {
        finish();
        return;
      }

      stage = document.createElement('div');
      stage.setAttribute('aria-hidden', 'true');
      stage.style.cssText =
        'position:fixed;left:0;top:0;width:0;height:0;overflow:hidden;opacity:0;pointer-events:none;z-index:-1;';
      const clones = sources.map((src) => {
        const img = document.createElement('img');
        const srcset = src.getAttribute('srcset');
        const sizes = src.getAttribute('sizes');
        if (srcset) img.setAttribute('srcset', srcset);
        if (sizes) img.setAttribute('sizes', sizes);
        img.setAttribute('src', src.getAttribute('src') || '');
        img.loading = 'eager';
        stage.appendChild(img);
        return img;
      });
      document.body.appendChild(stage);

      if (typeof clones[0].decode !== 'function') {
        finish();
        return;
      }
      const decoded = Promise.all(clones.map((img) => img.decode().catch(() => {})));
      // Generous safety cap: the old grid stays visible the whole time (no blank to
      // race against), so this only bounds a pathologically slow/broken image.
      const cap = new Promise((resolve) => setTimeout(resolve, 1500));
      Promise.race([decoded, cap]).then(finish);
    } catch (e) {
      finish();
    }
  }

  setLoading(isLoading) {
    if (!this.gridContainer) return;
    this.gridContainer.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    const collection = this.gridContainer.querySelector('.collection');
    if (collection) collection.classList.toggle('loading', isLoading);
  }

  // Pagination links live inside the AJAX-swapped grid. Use delegation on the
  // grid container so we don't need to rebind after every swap.
  onPaginationClick(event) {
    const link = event.target.closest('a.pagination__item');
    if (!link) return;
    if (link.getAttribute('aria-disabled') === 'true') return;
    const href = link.getAttribute('href');
    if (!href) return;

    event.preventDefault();

    try {
      const parsedUrl = new URL(href, window.location.origin);
      // fetchAndSwap re-adds the right section_id for the active collection.
      parsedUrl.searchParams.delete('section_id');
      this.fetchAndSwap(parsedUrl.searchParams.toString());
      this.scrollToTop();
    } catch (e) {
      console.error('homepage-collection-experience pagination click failed:', e);
    }
  }

  scrollToTop() {
    const rect = this.getBoundingClientRect();
    const top = rect.top + window.scrollY - 16;
    window.scrollTo({ top, behavior: 'smooth' });
  }
}

if (!customElements.get('homepage-collection-experience')) {
  customElements.define('homepage-collection-experience', HomepageCollectionExperience);
}
